"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelDrawableMarker = void 0;
var tslib_1 = require("tslib");
var core_api_1 = require("@ikegps/core-api");
var mobx_1 = require("mobx");
var id_1 = require("../../../id");
var vector_1 = require("../../../math/vector");
var unit_1 = require("../../../unit");
var drawable_1 = require("./drawable");
var label_drawable_1 = require("./label.drawable");
var marker_offset_drawable_1 = require("./marker.offset.drawable");
var CollectionModelDrawableMarker = /** @class */ (function (_super) {
    tslib_1.__extends(CollectionModelDrawableMarker, _super);
    function CollectionModelDrawableMarker(collectionModelDrawableState, drawableId, y, labelId, type, insight, pair, offset) {
        if (type === void 0) { type = core_api_1.MarkerType.NONE; }
        if (offset === void 0) { offset = 0; }
        var _this = _super.call(this, collectionModelDrawableState, drawableId) || this;
        _this.y = y;
        _this._labelId = labelId;
        _this.type = type;
        _this.insight = insight;
        _this._pair = pair;
        _this._offset = offset;
        return _this;
    }
    CollectionModelDrawableMarker.createWithLabel = function (drawableState, markerPoint, labelPoint, id, type, insight) {
        if (id === void 0) { id = id_1.IkeId.generate(); }
        if (type === void 0) { type = core_api_1.MarkerType.NONE; }
        var labelId = "".concat(id, ":label");
        var marker = new CollectionModelDrawableMarker(drawableState, id, markerPoint.y, labelId, type, insight);
        var label = new label_drawable_1.CollectionModelDrawableLabel(drawableState, labelId, labelPoint.x, labelPoint.y, marker.id, undefined, undefined);
        return { marker: marker, label: label };
    };
    Object.defineProperty(CollectionModelDrawableMarker.prototype, "x", {
        get: function () {
            // Markers must always be in the center of the image
            if (this.drawableState.capture != null) {
                return this.drawableState.capture.resolution.width / 2;
            }
            return 0;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Move the marker to this location
     *
     * **Markers can only be moved vertically**
     *
     * @param x coordinates in pixels
     * @param y coordinates in pixels
     */
    CollectionModelDrawableMarker.prototype.move = function (x, y) {
        var bounds = this.getPointInBounds(this.x, y);
        this.y = bounds.y;
    };
    CollectionModelDrawableMarker.prototype.moveNoBounds = function (x, y) {
        this.y = y;
    };
    Object.defineProperty(CollectionModelDrawableMarker.prototype, "label", {
        get: function () {
            return this.drawableState.getDrawable(this._labelId);
        },
        enumerable: false,
        configurable: true
    });
    CollectionModelDrawableMarker.prototype.getLabelConnectionPoint = function (options) {
        return {
            x: this.label.isOnLeftSide()
                ? this.x - options.draw.state.markerOffsetWidth // Left
                : this.x + options.draw.state.markerOffsetWidth, // Right
            y: this.y
        };
    };
    Object.defineProperty(CollectionModelDrawableMarker.prototype, "offset", {
        get: function () {
            return this._offset;
        },
        set: function (offset) {
            if (!CollectionModelDrawableMarker.isBase(this)) {
                throw new Error('Cannot set offset on non base marker');
            }
            if (offset < 0) {
                throw new Error("Offset cannot be a negative number; got: ".concat(offset));
            }
            var baseOffset = this.drawableState.baseOffset;
            if (offset === 0) {
                // Remove old base offset marker
                baseOffset === null || baseOffset === void 0 ? void 0 : baseOffset.remove();
                this._offset = 0;
                return;
            }
            this._offset = offset;
            // Create new base offset marker if it doesn't exist
            if (baseOffset == null) {
                marker_offset_drawable_1.CollectionModelDrawableMarkerOffset.createWithLabel(this.drawableState);
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(CollectionModelDrawableMarker.prototype, "pair", {
        /**
         * Pair links two markers together -- it is used by when linked to vertical separation fields.
         */
        get: function () {
            return this._pair;
        },
        set: function (value) {
            this._pair = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(CollectionModelDrawableMarker.prototype, "isInsightMeasurement", {
        get: function () {
            var _a;
            // True when insight state exists and is not applied.
            // False when insight state doesn't exist, or it exists but is applied.
            return this.insight ? !((_a = this.insight) === null || _a === void 0 ? void 0 : _a.applied) : false;
        },
        enumerable: false,
        configurable: true
    });
    CollectionModelDrawableMarker.prototype.isApprovableInsightDrawable = function () {
        return true;
    };
    Object.defineProperty(CollectionModelDrawableMarker.prototype, "isVisible", {
        get: function () {
            var _a;
            if (this.isInsightMeasurement) {
                // Show applied insight drawables
                if ((_a = this.insight) === null || _a === void 0 ? void 0 : _a.applied) {
                    return true;
                }
                // If the image already has a base, then hide unapplied insight base/offset markers
                if (this.collectionHasBase) {
                    return !this.isInsightBaseOffsetDetectionClass;
                }
            }
            return true;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(CollectionModelDrawableMarker.prototype, "isInsightBaseOffsetDetectionClass", {
        /**
         * Shortcut to check if the insight detection class is a base offset marker.
         */
        get: function () {
            var _a;
            if (!((_a = this.insight) === null || _a === void 0 ? void 0 : _a.detection_class)) {
                return false;
            }
            return (0, drawable_1.isInsightOffsetDetectionClass)(this.insight.detection_class);
        },
        enumerable: false,
        configurable: true
    });
    CollectionModelDrawableMarker.prototype.approveInsightFlag = function () {
        if (!this.insight || this.insight.applied) {
            return;
        }
        this.insight.applied = true;
        // If approving a base offset detection, set the offset value.
        if (this.isInsightBaseOffsetDetectionClass) {
            if (this.drawableState.base) {
                throw new Error('Attempted to approve a base offset but a base already exists');
            }
            var offsetValue = 0;
            switch (this.insight.detection_class) {
                case drawable_1.InsightMarkerDetectionClass.NineFeetMarker:
                    offsetValue = 9;
                    break;
                case drawable_1.InsightMarkerDetectionClass.SixFeetMarker:
                    offsetValue = 6;
                    break;
                case drawable_1.InsightMarkerDetectionClass.ThreeFeetMarker:
                    offsetValue = 3;
                    break;
                default:
                    break;
            }
            this.type = core_api_1.MarkerType.BASE;
            this.offset = unit_1.IkeUnit.convert(offsetValue, unit_1.IkeUnit.METER, unit_1.IkeUnit.FEET, core_api_1.IkeUnitType.DISTANCE);
        }
    };
    CollectionModelDrawableMarker.isMarker = function (t) {
        return t instanceof CollectionModelDrawableMarker;
    };
    CollectionModelDrawableMarker.isBase = function (t) {
        return CollectionModelDrawableMarker.isMarker(t) && t.type === core_api_1.MarkerType.BASE;
    };
    /** Is this marker just a standard marker */
    CollectionModelDrawableMarker.isNone = function (t) {
        return CollectionModelDrawableMarker.isMarker(t) && t.type === core_api_1.MarkerType.NONE;
    };
    Object.defineProperty(CollectionModelDrawableMarker.prototype, "value", {
        // TODO: Rename this to something easier to understand, like "offset" or "height"
        get: function () {
            if (this.type === core_api_1.MarkerType.BASE) {
                return this.offset || 0;
            }
            if (this.type === core_api_1.MarkerType.BASE_OFFSET) {
                return 0;
            }
            return this.calculateValue();
        },
        enumerable: false,
        configurable: true
    });
    CollectionModelDrawableMarker.prototype.remove = function () {
        // Remove this marker's label too, unless it has already been removed
        if (this.drawableState.hasDrawable(this._labelId)) {
            this.label.remove();
        }
        _super.prototype.remove.call(this);
    };
    CollectionModelDrawableMarker.prototype.calculateM2Px = function (heightMeters) {
        // if a base offset exists, we should use that measurement instead of the base
        var baseMarker = this.drawableState.baseOffset || this.drawableState.base;
        if (baseMarker == null) {
            return null;
        }
        var baseMarkerXY = baseMarker.getVector();
        var moveVector = new vector_1.Vector(0, -heightMeters);
        var moveDistance = this.drawableState.collection.state
            .getGeometry(this.captureId)
            .movePoint(baseMarkerXY, moveVector);
        return moveDistance;
    };
    CollectionModelDrawableMarker.prototype.calculateValue = function () {
        var baseMarker = this.drawableState.base;
        if (baseMarker == null) {
            return null;
        }
        var adjustOffset = baseMarker.offset || 0;
        var baseMarkerXY = baseMarker.getVector();
        var thisXY = this.getVector();
        var distance = this.drawableState.collection.state
            .getGeometry(this.captureId)
            .calculateDistance(baseMarkerXY, thisXY);
        if (baseMarkerXY.y < thisXY.y) {
            return adjustOffset - distance;
        }
        return adjustOffset + distance;
    };
    CollectionModelDrawableMarker.prototype.verticalDistanceFromLaser = function () {
        // laser point is assumed to be in the center of the image
        var capture = this.drawableState.capture;
        if (!capture) {
            return null;
        }
        var centerHeight = capture.resolution.height / 2;
        var markerHeight = this.getVector().y;
        var distance = this.drawableState.collection.state
            .getGeometry(this.captureId)
            .calculateWorldDifferenceVector(new vector_1.Vector(0, markerHeight), new vector_1.Vector(0, centerHeight)).y;
        return distance;
    };
    Object.defineProperty(CollectionModelDrawableMarker.prototype, "altitude", {
        get: function () {
            var _a, _b, _c;
            return (_b = (_a = this.drawableState.capture) === null || _a === void 0 ? void 0 : _a.target.altitude) !== null && _b !== void 0 ? _b : 0 + ((_c = this.verticalDistanceFromLaser()) !== null && _c !== void 0 ? _c : 0);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(CollectionModelDrawableMarker.prototype, "isDeletable", {
        get: function () {
            return this.type === core_api_1.MarkerType.NONE;
        },
        enumerable: false,
        configurable: true
    });
    CollectionModelDrawableMarker.prototype.clone = function (newParentDrawableState) {
        if (!newParentDrawableState.hasDrawable(this._labelId)) {
            this.label.clone(newParentDrawableState);
        }
        return new CollectionModelDrawableMarker(newParentDrawableState, this.id, this.y, this._labelId, this.type, this.insight ? tslib_1.__assign({}, this.insight) : undefined, this.pair, this._offset);
    };
    Object.defineProperty(CollectionModelDrawableMarker.prototype, "pairArray", {
        get: function () {
            var _a;
            return typeof this._pair === 'string' ? [this._pair] : (_a = this._pair) !== null && _a !== void 0 ? _a : [];
        },
        enumerable: false,
        configurable: true
    });
    CollectionModelDrawableMarker.prototype.equals = function (other) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return (other instanceof CollectionModelDrawableMarker
            && this.type === other.type
            && this.y === other.y
            && this._labelId === other._labelId
            && this._offset === other._offset
            && this.pairArray.join(',') === other.pairArray.join(',')
            && ((_a = this.insight) === null || _a === void 0 ? void 0 : _a.applied) === ((_b = other.insight) === null || _b === void 0 ? void 0 : _b.applied)
            && ((_c = this.insight) === null || _c === void 0 ? void 0 : _c.detection_class) === ((_d = other.insight) === null || _d === void 0 ? void 0 : _d.detection_class)
            && ((_e = this.insight) === null || _e === void 0 ? void 0 : _e.detection_id) === ((_f = other.insight) === null || _f === void 0 ? void 0 : _f.detection_id)
            && ((_g = this.insight) === null || _g === void 0 ? void 0 : _g.detection_version) === ((_h = other.insight) === null || _h === void 0 ? void 0 : _h.detection_version)
            && this._equals(other));
    };
    tslib_1.__decorate([
        mobx_1.observable
    ], CollectionModelDrawableMarker.prototype, "y", void 0);
    tslib_1.__decorate([
        mobx_1.action
    ], CollectionModelDrawableMarker.prototype, "move", null);
    tslib_1.__decorate([
        mobx_1.action
    ], CollectionModelDrawableMarker.prototype, "moveNoBounds", null);
    tslib_1.__decorate([
        mobx_1.observable
    ], CollectionModelDrawableMarker.prototype, "type", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], CollectionModelDrawableMarker.prototype, "insight", void 0);
    tslib_1.__decorate([
        mobx_1.computed
    ], CollectionModelDrawableMarker.prototype, "isInsightMeasurement", null);
    tslib_1.__decorate([
        mobx_1.action
    ], CollectionModelDrawableMarker.prototype, "approveInsightFlag", null);
    return CollectionModelDrawableMarker;
}(drawable_1.MoveableDrawable));
exports.CollectionModelDrawableMarker = CollectionModelDrawableMarker;
//# sourceMappingURL=marker.drawable.js.map